// "Observed so far" (DESIGN_SPEC §12.11; live only): plain sentences for the events that matter,
// chronological, each shown once with repeats folded into ×N. No severities, no technique ids.
//   observed(events, { t0, noise, max }) → { rows, total }   rows = the latest `max` distinct rows
import { categoryOf } from "../../lib/derive/categories";
import { classifyNoise } from "../../lib/derive/noise";
import { bytes, envPath, winBase, winDir } from "../../lib/format";
import type { Category, NoiseIndex } from "@/lib/derive/types";
import type { Event } from "@/types";
import { countsAsInjection } from "./derive/tree";

/** A run of inline text: id → a mono identifier, href → an in-page link, q → a quotation, at → a timestamp (epoch ms). */
export type Part = { text: string; id?: boolean; href?: string; q?: boolean; at?: number };

export type ObservedRow = {
  key: string;
  seq: number;
  /** epoch ms of the first occurrence */
  ts: number;
  category: Category;
  parts: Part[];
  count: number;
};

const T = (text: string): Part => ({ text });
const I = (text: string): Part => ({ text, id: true });
const str = (v: unknown): string => (typeof v === "string" ? v : v == null ? "" : String(v));

const EXEC_EXT = /\.(exe|dll|ps1|bat|cmd|vbs|js|hta|scr|lnk|msi)$/i;
const USER_DIR = /^([a-z]:\\users\\|[a-z]:\\programdata\\|%[a-z]+%\\)/i;

/** "HKCU\Software\Microsoft\Windows\CurrentVersion\Run\WinSvc" → "HKCU\…\CurrentVersion\Run\WinSvc". */
function shortKey(key: string): string {
  const parts = key.split("\\").filter(Boolean);
  if (parts.length <= 4) return key;
  return [parts[0], "…", ...parts.slice(-3)].join("\\");
}

/** schtasks … /tn "WinSvcUpdate" … → WinSvcUpdate */
function taskName(cmd: string): string | null {
  const m = /\/tn\s+(?:"([^"]+)"|'([^']+)'|(\S+))/i.exec(cmd);
  return m ? (m[1] ?? m[2] ?? m[3] ?? null) : null;
}

export function observed(
  events: readonly Event[],
  opts: { noise?: NoiseIndex; max?: number } = {},
): { rows: ObservedRow[]; total: number } {
  const noise = opts.noise ?? classifyNoise(events);
  const max = opts.max ?? 8;
  const rows = new Map<string, ObservedRow>();
  const names = new Map<number, string>();
  const dnsSeen = new Set<string>();

  const add = (e: Event, key: string, parts: Part[]) => {
    const cur = rows.get(key);
    if (cur) {
      cur.count++;
      return;
    }
    rows.set(key, { key, seq: e.seq, ts: Date.parse(e.ts), category: categoryOf(e.kind), parts, count: 1 });
  };

  for (const e of events) {
    const f = e.fields ?? {};
    switch (e.kind) {
      case "proc.start": {
        const pid = Number(f.pid);
        const name = winBase(str(f.image)) || `pid ${pid}`;
        names.set(pid, name);
        if (f.scope === false) break;
        const parent = names.get(Number(f.ppid)) ?? (winBase(str(f.parent_image)) || `pid ${str(f.ppid)}`);
        add(e, `proc:${pid}:${e.seq}`, [I(parent), T(" started "), I(name)]);
        const task = /\bschtasks(\.exe)?["']?\s.*\/create\b/i.test(str(f.cmdline)) ? taskName(str(f.cmdline)) : null;
        if (task) add(e, `task:${task.toLowerCase()}`, [T("Scheduled task created: "), I(task)]);
        break;
      }
      case "thread.inject": {
        const target = Number(f.target_pid);
        const creator = Number(f.creator_pid);
        if (!countsAsInjection(creator, target)) break;
        const tName = names.get(target) ?? (winBase(str(f.target_image)) || `pid ${target}`);
        const cName = names.get(creator) ?? (winBase(str(f.creator_image)) || `pid ${creator}`);
        add(e, `inj:${creator}>${target}`, [T("Thread written into "), I(tName), T(" from "), I(cName)]);
        break;
      }
      case "registry.set": {
        const key = str(f.key);
        if (/\\currentversion\\run(once)?(\\|$)/i.test(key)) add(e, `run:${key.toLowerCase()}`, [T("Run key set: "), I(shortKey(key))]);
        break;
      }
      case "file.create": {
        const path = str(f.path);
        if (!EXEC_EXT.test(path) || !USER_DIR.test(path)) break;
        add(e, `drop:${path.toLowerCase()}`, [T("Wrote "), I(winBase(path)), T(" to "), I(envPath(winDir(path)))]);
        break;
      }
      case "dump.taken": {
        const name = str(f.name) || `pid ${str(f.pid)}`;
        const size = Number(f.size);
        add(e, `dump:${name}`, [T("Process dump taken: "), I(name), ...(Number.isFinite(size) && size > 0 ? [T(` (${bytes(size)})`)] : [])]);
        break;
      }
      case "yara.match":
        add(e, `yara:${str(f.rule)}:${str(f.artifact)}`, [T("YARA "), I(str(f.rule)), T(" matched "), I(str(f.artifact))]);
        break;
      case "config.extracted": {
        const c2 = Array.isArray(f.c2) ? f.c2.map(str).filter(Boolean) : [];
        const family = str(f.family);
        add(e, `config:${str(f.extractor)}:${family}`, [T("Config extracted"), ...(family ? [T(": "), I(family)] : []), ...(c2[0] ? [T(" · C2 "), I(c2[0])] : []), T(` (${str(f.extractor) || "extractor"})`)]);
        break;
      }
      case "net.http": {
        if (noise.isBackground(e)) break;
        const method = str(f.method).toUpperCase() || "GET";
        const target = `${str(f.host)}${str(f.path)}`;
        add(e, `http:${method}:${target.toLowerCase()}`, [T(`${method} to `), I(target)]);
        break;
      }
      case "net.dns": {
        const name = str(f.name).toLowerCase().replace(/\.$/, "");
        const ip = str(f.ip);
        if (!name || !ip || dnsSeen.has(name) || noise.isBackground(e)) break;
        dnsSeen.add(name);
        add(e, `dns:${name}`, [T("Resolved "), I(name), T(" to "), I(ip)]);
        break;
      }
    }
  }
  const all = [...rows.values()];
  return { rows: all.slice(-max), total: all.length };
}
